import {
    ACCOUNTING_BILL_STATUS_BATCHED,
    ACCOUNTING_BILL_STATUS_PAID,
    ACCOUNTING_BILL_STATUSES,
} from "@/actuals/constants";
import ApiAutocomplete from "@/common/ApiAutocomplete";
import CustomTableContainer from "@/table/CustomTableContainer";
import { formattedCurrency, queryToFilterValue } from "@/utils";
import {
    Autocomplete,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Grid,
    Modal,
    Tab,
    TableCell,
    Tabs,
    TextField,
    useTheme,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import AccountingBill, { AccountingBillRow } from "./AccountingBill";
import { useDispatch } from "react-redux";
import {
    checkAllBills,
    checkBill,
    unCheckBill,
    clearSelectedBills,
    openPopup,
    selectAccountingBills,
    selectFilterStatus,
    selectSelectedBills,
    setAccountingBills,
    setFilterStatus,
    unCheckAllBills,
} from "./accountingBillsSlice";
import BatchActionConfirmationPopup from "./AccountingBillsConfirmationPopup";
import { useSelector } from "react-redux";
import { currencies } from "@/constants";
import AccountingBillsCreateBatchPopup from "./AccountingBillsCreateBatchPopup";
import AccountingBillBatch from "./AccountingBillBatch";
import { getCategories, getTaxCodes } from "@/actuals/services";
import { useQuery } from "react-query";

export default function AccountingBills() {
    const [tab, setTab] = useState(0);

    const refreshCallbacksRef = useRef({});
    const registerRefreshCallback = (type, callback) => {
        refreshCallbacksRef.current[type] = callback;
    };
    const invokeRefreshCallbacks = () => {
        Object.values(refreshCallbacksRef.current).forEach((callback) => {
            if (typeof callback === "function") {
                callback();
            }
        });
    };

    return (
        <div>
            <Tabs
                sx={{
                    borderBottom: 1,
                    color: "grey",
                    marginBottom: 3,
                    minHeight: 0,
                    padding: 0,
                }}
                value={tab}
                onChange={(event, newValue) => setTab(newValue)}
            >
                <Tab
                    sx={{
                        fontSize: 12,
                        minWidth: 20,
                        minHeight: 0,
                        paddingLeft: 2,
                        paddingRight: 2,
                    }}
                    label="Vendor Bills"
                    value={0}
                />
                <Tab
                    sx={{
                        fontSize: 12,
                        minWidth: 20,
                        minHeight: 0,
                        paddingLeft: 2,
                        paddingRight: 2,
                    }}
                    label="Batches"
                    value={1}
                />
            </Tabs>

            <div hidden={tab != 0}>
                <AccountingBillsTable
                    refresh={invokeRefreshCallbacks}
                    registerRefreshCallback={registerRefreshCallback}
                />
            </div>
            <div hidden={tab != 1}>
                <Batches
                    refresh={invokeRefreshCallbacks}
                    registerRefreshCallback={registerRefreshCallback}
                />
            </div>
        </div>
    );
}

function Batches({ registerRefreshCallback, refresh }) {
    return (
        <>
            <CustomTableContainer
                height={"60vh"}
                modelType="accounting-bill-batch"
                defaultOrder="-id"
                title="Batches"
                headCells={[
                    { id: "_checkbox", label: "" },
                    { id: "id", label: "Id" },
                    { id: "name", label: "Name" },
                    { id: "completion", label: "Completion" },
                    { id: "total", label: "Total" },
                    { id: "_actions", label: "" },
                ]}
                itemRowComponent={({ item }) => (
                    <AccountingBillBatch batchData={item} />
                )}
                filterComponents={({
                    filters,
                    setFilters,
                    handleInputChange,
                    handleAutocompleteChange,
                    handleAutocompleteClear,
                }) => {
                    return <></>;
                }}
                customBottomLeftComponent={({}) => (
                    <Grid
                        container
                        alignItems={"center"}
                        spacing={2}
                        paddingTop={3}
                    >
                        <Grid item>
                            <BatchActionConfirmationPopup refresh={refresh} />
                        </Grid>
                        <Grid item>
                            <CheckedTotal />
                        </Grid>
                    </Grid>
                )}
                onFilterChange={(getItems) => {
                    registerRefreshCallback("batch", getItems);
                }}
            >
                <Helmet>
                    <title>Accounting Bill Batches - Makers Central</title>
                </Helmet>
            </CustomTableContainer>
        </>
    );
}

function AccountingBillsTable({ registerRefreshCallback, refresh }) {
    const dispatch = useDispatch();
    const handleLoaded = (items) => {
        dispatch(setAccountingBills(items));
    };
    const accountingBills = useSelector(selectAccountingBills);

    const resTaxCode =
        useQuery({
            queryKey: ["accounting-tax-codes"],
            queryFn: getTaxCodes,
            refetchOnWindowFocus: false,
        }).data || {};
    const taxCodes = resTaxCode.data || [];

    const resCategories =
        useQuery({
            queryKey: ["accounting-account"],
            queryFn: getCategories,
            refetchOnWindowFocus: false,
        }).data || {};
    const categories = resCategories.data || [];

    return (
        <>
            <CustomTableContainer
                height={"60vh"}
                modelType="accounting-bill"
                defaultOrder="-id"
                title="Accounting Bills"
                headCells={[
                    {
                        id: "_checkbox",
                        label: "",
                        labelComponent: (
                            <CheckAllBox accountingBills={accountingBills} />
                        ),
                    },
                    { id: "vendor_name", label: "Vendor Name" },
                    { id: "accounting_bill_number", label: "Invoice Number" },
                    { id: "status", label: "Status" },
                    { id: "actual", label: "Bill Amount" },
                    { id: "actual_project", label: "Project Amount" },
                    { id: "legal_entity_name", label: "Legal Entity" },
                    { id: "_actions", label: "" },
                ]}
                itemRowComponent={({ item, deleteItem }) => {
                    return (
                        <AccountingBillRow
                            item={item}
                            deleteItem={deleteItem}
                            categories={categories}
                            taxCodes={taxCodes}
                        />
                    );
                }}
                filterComponents={({
                    filters,
                    setFilters,
                    handleInputChange,
                    handleAutocompleteChange,
                    handleAutocompleteClear,
                }) => {
                    return (
                        <Grid container sx={{ paddingTop: 2 }} spacing={3}>
                            <Grid item xs={6} md={3}>
                                <Autocomplete
                                    value={queryToFilterValue(filters.status)}
                                    disablePortal
                                    options={ACCOUNTING_BILL_STATUSES}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Status" />
                                    )}
                                    onChange={(event, newVal) => {
                                        (dispatch(setFilterStatus(newVal)),
                                            dispatch(clearSelectedBills()));
                                        handleAutocompleteChange("status")(
                                            event,
                                            newVal,
                                        );
                                    }}
                                    onInputChange={(event, newVal, reason) => {
                                        dispatch(setFilterStatus(null));
                                        dispatch(clearSelectedBills());
                                        handleAutocompleteClear("status")(
                                            event,
                                            newVal,
                                            reason,
                                        );
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <ApiAutocomplete
                                    silentErrors
                                    value={queryToFilterValue(
                                        filters.project_id,
                                    )}
                                    apiRoute={"projects"}
                                    fields={["name"]}
                                    onChange={(event, newVal) => {
                                        dispatch(clearSelectedBills());
                                        handleAutocompleteChange("project_id")(
                                            event,
                                            newVal,
                                        );
                                    }}
                                    onInputChange={(event, newVal, reason) => {
                                        dispatch(clearSelectedBills());
                                        handleAutocompleteClear("project_id")(
                                            event,
                                            newVal,
                                            reason,
                                        );
                                    }}
                                    label="Project"
                                    simple={true}
                                />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Autocomplete
                                    options={currencies}
                                    value={queryToFilterValue(filters.currency)}
                                    onChange={(event, newVal) => {
                                        dispatch(clearSelectedBills());
                                        handleAutocompleteChange("currency")(
                                            event,
                                            newVal,
                                        );
                                    }}
                                    onInputChange={(event, newVal, reason) => {
                                        dispatch(clearSelectedBills());
                                        handleAutocompleteClear("currency")(
                                            event,
                                            newVal,
                                            reason,
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Bill Currency"
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <ApiAutocomplete
                                    silentErrors
                                    value={queryToFilterValue(
                                        filters.legal_entity_id,
                                    )}
                                    apiRoute={"legal-entities"}
                                    fields={["name"]}
                                    onChange={(event, newVal) => {
                                        dispatch(clearSelectedBills());
                                        handleAutocompleteChange(
                                            "legal_entity_id",
                                        )(event, newVal);
                                    }}
                                    onInputChange={(event, newVal, reason) => {
                                        dispatch(clearSelectedBills());
                                        handleAutocompleteClear(
                                            "legal_entity_id",
                                        )(event, newVal, reason);
                                    }}
                                    label="Legal Entity"
                                    simple={true}
                                />
                            </Grid>
                        </Grid>
                    );
                }}
                customBottomLeftComponent={({ getItem }) => (
                    <Grid
                        container
                        alignItems={"center"}
                        spacing={2}
                        paddingTop={3}
                    >
                        <Grid item>
                            <BatchActionConfirmationPopup refresh={refresh} />
                        </Grid>
                        <Grid item>
                            <AccountingBillsCreateBatchPopup
                                refresh={refresh}
                            />
                        </Grid>
                        <Grid item>
                            <CheckedTotal />
                        </Grid>
                    </Grid>
                )}
                onLoaded={handleLoaded}
                onFilterChange={(getItems) => {
                    registerRefreshCallback("accounting-bill", getItems);
                }}
            >
                <Helmet>
                    <title>Accounting Bills - Makers Central</title>
                </Helmet>
            </CustomTableContainer>
        </>
    );
}

export const CheckedTotal = () => {
    const selectedBills = Object.values(useSelector(selectSelectedBills));
    const sameBillCurrency = selectedBills.every((bill) => {
        return bill.currency == selectedBills[0].currency;
    });
    let total = 0;
    selectedBills.forEach((bill) => {
        total += bill.total;
    });
    if (selectedBills.length == 0) {
        return null;
    }
    return (
        <span>
            {sameBillCurrency
                ? `Total: ${formattedCurrency(total, selectedBills[0].currency)} `
                : "Mixed Currency"}
        </span>
    );
};

export const CheckAllBox = ({ accountingBills = [] }) => {
    const dispatch = useDispatch();
    const filterStatus = useSelector(selectFilterStatus);
    const selectedBills = useSelector(selectSelectedBills);
    const selectedBillIdSet = new Set(
        Object.keys(selectedBills).map((billId) => parseInt(billId)),
    );
    const accountingBillIds = accountingBills.map((bill) => bill.id);
    const allChecked = accountingBillIds.every((billId) =>
        selectedBillIdSet.has(billId),
    );
    const someChecked =
        !allChecked &&
        accountingBillIds.some((billId) => selectedBillIdSet.has(billId));
    if (
        filterStatus == ACCOUNTING_BILL_STATUS_PAID ||
        Object.values(accountingBills).length == 0
    ) {
        return null;
    }
    const handleCheck = (event, checked) => {
        console.log(checked);
        if (checked) {
            accountingBills.forEach((bill) => {
                dispatch(checkBill(bill));
            });
        } else {
            accountingBills.forEach((bill) => {
                dispatch(unCheckBill(bill));
            });
        }
    };
    return (
        <Checkbox
            onChange={handleCheck}
            checked={allChecked}
            indeterminate={someChecked}
        />
    );
};
